/**
 * A block's form, **drawn from the service's own schema** (dukaan §3 step 3).
 *
 * `GET /api/v1/site/vocabulary` returns the nine block schemas field by field —
 * name, kind, label, limit, choices — and this file turns each `kind` into a
 * control. There is no per-block component and no list of fields in this
 * bundle, which is the point: `app/domain/site.py: validate_block` is the only
 * validator, and a form built from a *second* description of the same blocks
 * would disagree with it the day one of them changed. The firm would
 * experience that as "it saved and then it did not".
 *
 * So this file knows about **kinds**, not about blocks. Adding a field to a
 * block is a change in one Python tuple; adding a *kind* is the only thing
 * that reaches here.
 *
 * **Nothing here validates.** The service refuses and says which block, which
 * field and why (`blockIssue`), and the editor scrolls to it. Two validators
 * that agree today are two validators that disagree in a year, and on this
 * module the one the firm sees would be the one letting a script through
 * (ADR-0013 §4).
 *
 * **C17 (Wave 18D, T-4.74, ADR-0036 §2, §7):**
 *
 * - **`datetime`** — a date-and-time input **in IST** (C20, spec §10.12
 *   item 5, T-4.82: the offset chooser is gone), stored as ISO 8601 at
 *   `+05:30` (`2026-12-31T19:00+05:30`). A value saved earlier with another
 *   offset is shown converted to IST. The service refuses a value without one.
 * - **Nested items, one level deep** — a schedule's days of sessions, a
 *   ticket tier's perks. `MAX_ITEMS_DEPTH` is two, and a schema that nests
 *   deeper is refused here, drawn as a note rather than as a third level.
 * - **Anchor and label** — every section's `anchor` / `anchor_label`, drawn
 *   together at the end under *Link to this section* rather than among the
 *   section's own content.
 *
 * **C20 (Wave 20D, T-4.78, ADR-0041 §1):** **`buttons`** — up to two rows of
 * a label and a link of four kinds (`ButtonsField`). It replaced the
 * `cta_*` / `secondary_*` pairs, and with them the booking picker that sat
 * under a `*_href` box.
 *
 * **C21 (Wave 21G, T-4.90, ADR-0043 §2):**
 *
 * - **`min_items`** — a list's floor. A row cannot be removed below it (the
 *   add button was already disabled at `max_items`), and the count says so.
 * - **A `rich` field's `max_length`** — a counter under the control, counted
 *   as the service counts it: the characters a visitor reads (`richLength`).
 * - **`comparison`** — its `columns` and `rows` drawn together as one grid
 *   (`CellGridField`), from the `CELL_GRIDS` lookup, as `MEASURED_IMAGES` is.
 * **C23 (Wave 25D, T-4.114, ADR-0045 §5):** **`icon`** — the firm's pick from
 * the curated set, drawn as `IconPicker` (inside the items of `feature_grid`,
 * `metrics` and `steps`). The set is the vocabulary's `icons`, read from the
 * builder's `useSite()` or handed in as `icons`; *No icon* stores nothing
 * (the field is absent), and nothing is ever defaulted (§4).
 *
 * **C25 (Wave 27E, T-4.135, ADR-0054 §5):** **`slugs`** — the `ServicePicker`
 * over the firm's catalogue, handed down by `ServiceCatalogProvider` as the
 * icon set is; the box is kept for a user who cannot read the catalogue.
 *
 * **C27 (ADR-0058, UAT report #8):** a `slugs` field's **pick** —
 * `service_pick`, or `form_service_pick` beside `form_service_slugs` — is not
 * drawn as a field of its own. The picker owns it and writes it with the slugs
 * in one change (`pickFieldFor`), so *Choose services* with none ticked is
 * stored as a choice and draws nothing, instead of meaning all.
 */

import { Button, Checkbox, Field, Input, Select, Textarea } from '@fiveox/ui'
import { Plus, Trash2 } from 'lucide-react'
import type { ReactNode } from 'react'

import { IconPicker, IconSetProvider, useIconSet } from '../../../components/IconPicker'
import type { BlockSchema, FieldSchema, IconSet, RichText } from '../../../lib/api/site'
import { useOptionalSite } from '../useSite'
import { ButtonsField } from './ButtonsField'
import { CELL_GRIDS, CellGridField } from './CellGridField'
import { ImageField, type Dimensions } from './ImageField'
import { RichTextField } from './RichTextField'
import { asServicePick, ServicePicker, useServiceCatalog, type ServicePick } from './ServicePicker'

type Props = Record<string, unknown>

/**
 * **Two levels of lists, and no more** (ADR-0036 §2): a list, and a list
 * inside each of its rows. The service's schema language could say more; this
 * editor refuses to, because a third level on a phone is a form nobody can use.
 */
export const MAX_ITEMS_DEPTH = 2

/** How deep a field's lists go — `0` for a field that is not a list. */
export function itemsDepth(field: Pick<FieldSchema, 'kind' | 'of'>): number {
  if (field.kind !== 'items') return 0
  return 1 + Math.max(0, ...(field.of ?? []).map(itemsDepth))
}

/**
 * The characters a visitor reads in a rich document — every span of every
 * node and list item, **counted in code points** as the service's
 * `_rich_length` counts them (Python's `len`), so an emoji is one.
 */
export function richLength(value: unknown): number {
  const doc = value as RichText | null | undefined
  let total = 0
  for (const node of doc?.nodes ?? []) {
    const groups = [node.spans ?? [], ...(node.items ?? []).map((item) => item.spans ?? [])]
    for (const spans of groups) for (const span of spans) total += [...(span.text ?? '')].length
  }
  return total
}

/** The fields `BlockForm` draws last, under *Link to this section* (C17). */
export const ANCHOR_FIELDS = ['anchor', 'anchor_label'] as const

/**
 * The one offset a `datetime` is stored at — IST (C20, spec §10.12 item 5).
 * Staff enter the time in IST and never choose an offset.
 */
export const IST_OFFSET = '+05:30'
/** Kept under its C17 name, for the readers that knew it. */
export const DEFAULT_OFFSET = IST_OFFSET

const IST_MINUTES = 5 * 60 + 30
const ISO_WITH_OFFSET = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})(?::\d{2})?(Z|[+-]\d{2}:\d{2})$/

/**
 * A stored instant as the IST wall-clock time the input shows —
 * `YYYY-MM-DDTHH:mm`, or `''` for anything that is not an instant. An instant
 * stored at another offset (before C20 the chooser allowed one) is **the same
 * moment, converted**: `2026-12-31T19:00+04:00` is `2026-12-31T20:30` in IST.
 */
export function istLocal(value: unknown): string {
  if (typeof value !== 'string') return ''
  const match = ISO_WITH_OFFSET.exec(value.trim())
  if (!match) return ''
  if (match[2] === IST_OFFSET) return match[1] as string
  const at = Date.parse(value.trim())
  if (Number.isNaN(at)) return ''
  return new Date(at + IST_MINUTES * 60 * 1000).toISOString().slice(0, 16)
}

/** The input's IST time, as the instant the service stores — `undefined` (absent) while it is empty. */
export function fromIstLocal(local: string): string | undefined {
  return local ? `${local.slice(0, 16)}${IST_OFFSET}` : undefined
}

/**
 * Which field holds the width and which the height, for a schema that has
 * both. The `image` block is the only one today (Wave 3 close §6.1) and this is
 * written as a lookup rather than as `if (type === 'image')` so a second block
 * that needs a measured picture is one line.
 */
const MEASURED_IMAGES: Record<string, { url: string; width: string; height: string }> = {
  image: { url: 'image_url', width: 'width_px', height: 'height_px' },
}

/**
 * The pick that rides beside a `slugs` field, when the schema has one
 * (ADR-0058 §1): `service_slugs` → `service_pick`, `form_service_slugs` →
 * `form_service_pick`. `null` on `pricing` and `product_spotlight`, which
 * require a choice and so carry none.
 */
export function pickFieldFor(schema: BlockSchema, field: FieldSchema): string | null {
  if (field.kind !== 'slugs') return null
  const name = field.name.replace(/slugs$/, 'pick')
  return schema.fields.some((f) => f.name === name && f.kind === 'enum') ? name : null
}

export function BlockForm({
  schema,
  value,
  onChange,
  issue,
  icons,
}: {
  schema: BlockSchema
  value: Props
  onChange: (next: Props) => void
  /** The field the service refused, if it refused one on this block. */
  issue?: { field: string | null; reason: string | null } | null
  /** C23: the icon set for `icon` fields — the builder's vocabulary when not given. */
  icons?: IconSet | null
}) {
  const fromSite = useOptionalSite()?.vocabulary?.icons ?? null
  const set = (name: string, next: unknown) => onChange({ ...value, [name]: next })
  const measured = MEASURED_IMAGES[schema.type]
  const isAnchor = (name: string) => (ANCHOR_FIELDS as readonly string[]).includes(name)
  const own = schema.fields.filter((f) => !isAnchor(f.name))
  const anchorFields = schema.fields.filter((f) => isAnchor(f.name))
  // C21: a table's two lists, drawn once, together, where its columns are.
  const grid = CELL_GRIDS[schema.type]
  const gridColumns = grid ? own.find((f) => f.name === grid.columns && f.kind === 'items') : undefined
  const gridRows = grid ? own.find((f) => f.name === grid.rows && f.kind === 'items') : undefined
  // C27: the picks the service picker owns, drawn by it and not as a select.
  const picks = new Set(own.map((f) => pickFieldFor(schema, f)).filter((n): n is string => n !== null))

  return (
    <IconSetProvider value={icons ?? fromSite}>
    <div className="space-y-3">
      {own.map((field) => {
        if (grid && gridColumns && gridRows) {
          if (field === gridRows) return null
          if (field === gridColumns) {
            return (
              <CellGridField
                key={field.name}
                grid={grid}
                columnsField={gridColumns}
                rowsField={gridRows}
                value={value}
                error={
                  issue && (issue.field === gridColumns.name || issue.field === gridRows.name)
                    ? (issue.reason ?? 'That is not allowed here.')
                    : null
                }
                onChange={(patch) => onChange({ ...value, ...patch })}
              />
            )
          }
        }
        if (picks.has(field.name)) return null
        const pickName = pickFieldFor(schema, field)
        if (pickName) {
          return (
            <SlugsField
              key={field.name}
              field={field}
              value={value[field.name]}
              pick={asServicePick(value[pickName])}
              empties={schema.type === 'services' ? 'section' : 'form'}
              error={
                issue && (issue.field === field.name || issue.field === pickName)
                  ? (issue.reason ?? 'That is not allowed here.')
                  : null
              }
              onChange={(next, pick) =>
                onChange(pick ? { ...value, [field.name]: next, [pickName]: pick } : { ...value, [field.name]: next })
              }
            />
          )
        }
        return (
          <OneField
            key={field.name}
            field={field}
            value={value[field.name]}
            error={issue?.field === field.name ? (issue.reason ?? 'That is not allowed here.') : null}
            onChange={(next) => set(field.name, next)}
            onMeasured={
              measured && field.name === measured.url
                ? (dimensions: Dimensions | null) =>
                    // Both or neither. Keeping a stale width beside a new picture
                    // reserves a box of the wrong shape, which is worse than no
                    // box: it is silent and it looks deliberate.
                    onChange({
                      ...value,
                      [measured.width]: dimensions?.width,
                      [measured.height]: dimensions?.height,
                    })
                : undefined
            }
          />
        )
      })}
      {anchorFields.length > 0 && (
        <fieldset className="space-y-2.5 rounded-lg border border-border p-3" data-anchor-fields="">
          <legend className="px-1 text-sm font-medium text-foreground">Link to this section</legend>
          <p className="text-xs text-muted-foreground">
            Optional. An address lets a menu link jump here — lower-case letters, numbers and
            hyphens, like <code className="font-mono">tickets</code>. A menu label puts it in a
            one-page menu.
          </p>
          <div className="grid gap-2.5 sm:grid-cols-2">
            {anchorFields.map((field) => (
              <OneField
                key={field.name}
                field={field}
                value={value[field.name]}
                error={issue?.field === field.name ? (issue.reason ?? 'That is not allowed here.') : null}
                onChange={(next) => set(field.name, next)}
              />
            ))}
          </div>
        </fieldset>
      )}
    </div>
    </IconSetProvider>
  )
}

function OneField({
  field,
  value,
  error,
  onChange,
  onMeasured,
  depth = 0,
}: {
  field: FieldSchema
  value: unknown
  error: string | null
  onChange: (next: unknown) => void
  onMeasured?: (dimensions: Dimensions | null) => void
  /** How many lists this field is already inside. */
  depth?: number
}) {
  const hint = field.max_length ? `Up to ${field.max_length} characters.` : undefined

  switch (field.kind) {
    case 'text':
    case 'url': {
      return (
        <Field label={field.label} hint={hint} error={error} labelAside={aside(field)}>
          {(control) => (
            <Input
              {...control}
              value={asString(value)}
              maxLength={field.max_length}
              inputMode={field.kind === 'url' ? 'url' : undefined}
              onChange={(e) => onChange(e.target.value)}
            />
          )}
        </Field>
      )
    }

    case 'long_text':
      return (
        <Field label={field.label} hint={hint} error={error} labelAside={aside(field)}>
          {(control) => (
            <Textarea
              {...control}
              rows={3}
              value={asString(value)}
              maxLength={field.max_length}
              onChange={(e) => onChange(e.target.value)}
            />
          )}
        </Field>
      )

    case 'rich':
      return (
        <Field
          label={field.label}
          error={error}
          labelAside={aside(field)}
          // C21: counted as the service counts it — the words a visitor reads.
          hint={field.max_length ? `${richLength(value)} of ${field.max_length} characters.` : undefined}
        >
          <RichTextField value={value as RichText | null} onChange={onChange} />
        </Field>
      )

    case 'image':
      return (
        <ImageField
          label={field.label}
          value={asString(value)}
          onChange={onChange}
          onMeasured={onMeasured}
          required={field.required}
          hint={error ?? undefined}
        />
      )

    case 'bool':
      return (
        <div>
          <Checkbox
            label={field.label}
            checked={value === true}
            onChange={(e) => onChange(e.target.checked)}
          />
          {error && <p className="mt-1 text-xs text-destructive">{error}</p>}
        </div>
      )

    case 'int':
      return (
        <Field
          label={field.label}
          hint={`Between ${field.minimum ?? 0} and ${field.maximum ?? 0}.`}
          error={error}
        >
          {(control) => (
            <Input
              {...control}
              type="number"
              min={field.minimum}
              max={field.maximum}
              value={typeof value === 'number' ? String(value) : ''}
              // An empty box is **absent**, not zero. `validate_block` fills
              // the schema's own default for an absent field, and a firm that
              // clears "How many posts" means "the usual", not "none".
              onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
            />
          )}
        </Field>
      )

    case 'enum':
      return (
        <Field label={field.label} error={error}>
          {(control) => (
            <Select {...control} value={asString(value)} onChange={(e) => onChange(e.target.value)}>
              {(field.choices ?? []).map((choice) => (
                <option key={choice} value={choice}>
                  {sentence(choice)}
                </option>
              ))}
            </Select>
          )}
        </Field>
      )

    case 'slugs':
      // C25 (ADR-0054 §5): the picker over the firm's catalogue, or the box
      // for a user who cannot read it. Either way the value is the same list
      // of slugs (ADR-0022 §5).
      return <SlugsField field={field} value={value} error={error} onChange={onChange} />

    case 'items':
      // Two levels and no more (ADR-0036 §2). A third is refused here — drawn
      // as a note, never as a form — rather than half-drawn.
      if (depth + itemsDepth(field) > MAX_ITEMS_DEPTH) {
        return (
          <p className="text-xs text-muted-foreground" data-too-deep={field.name}>
            {field.label} is nested too deeply for this editor.
          </p>
        )
      }
      return (
        <ItemsField field={field} value={value} error={error} onChange={onChange} depth={depth} />
      )

    case 'datetime':
      return <DateTimeField field={field} value={value} error={error} onChange={onChange} />

    case 'buttons':
      // C20, ADR-0041 §1 — the section's own buttons, and no others.
      return <ButtonsField field={field} value={value} error={error} onChange={onChange} />

    case 'icon':
      // C23, ADR-0045 §5 — the firm's pick, or absent for *no icon*.
      return <IconField field={field} value={value} error={error} onChange={onChange} />

    default:
      // A kind the service grew and this bundle has not. Rendered as nothing
      // rather than as a broken control — the same rule the renderer keeps for
      // an unknown block type, and for the same reason.
      return null
  }
}

/**
 * A repeating group — a team's members, a page's questions.
 *
 * The sub-fields are the schema's own `of`, so this recurses through `OneField`
 * and knows nothing about what a member or a question is.
 */
function ItemsField({
  field,
  value,
  error,
  onChange,
  depth,
}: {
  field: FieldSchema
  value: unknown
  error: string | null
  onChange: (next: unknown) => void
  depth: number
}) {
  const rows = (Array.isArray(value) ? value : []) as Props[]
  const subFields = field.of ?? []
  const max = field.max_items ?? 24
  // C21: the list's floor (`tabs` 2, `marquee` 3). A row below it cannot be
  // removed; the service refuses a list shorter than it.
  const min = field.min_items ?? 0

  const setRow = (index: number, next: Props) =>
    onChange(rows.map((row, i) => (i === index ? next : row)))

  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-foreground">{field.label}</span>
        <span className="text-xs text-muted-foreground" data-items-count={field.name}>
          {rows.length} of {max}
          {min > 0 ? ` · at least ${min}` : ''}
        </span>
      </div>
      {error && <p className="mb-1.5 text-xs text-destructive">{error}</p>}

      <div className="space-y-2">
        {rows.map((row, index) => (
          <div key={index} className="rounded-lg border border-border bg-surface-raised p-3">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-xs font-medium text-muted-foreground">#{index + 1}</span>
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Remove ${field.label} ${index + 1}`}
                disabled={rows.length <= min}
                title={rows.length <= min ? `${field.label} needs at least ${min}.` : undefined}
                onClick={() => onChange(rows.filter((_, i) => i !== index))}
              >
                <Trash2 size={14} />
              </Button>
            </div>
            <div className="space-y-2.5">
              {subFields.map((sub) => (
                <OneField
                  key={sub.name}
                  field={sub}
                  value={row[sub.name]}
                  error={null}
                  depth={depth + 1}
                  onChange={(next) => setRow(index, { ...row, [sub.name]: next })}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      <Button
        variant="secondary"
        size="sm"
        className="mt-2"
        disabled={rows.length >= max}
        onClick={() => onChange([...rows, {}])}
      >
        <Plus size={14} /> Add
      </Button>
    </div>
  )
}

/**
 * **`datetime`** (C17, ADR-0036 §2; C20, T-4.82): one date-and-time input, **in
 * IST**, stored at `+05:30`. There is no offset chooser — *"user will select
 * date and time in ist"* (the owner, 2026-09-27) — and the browser's own time
 * zone is never consulted.
 */
function DateTimeField({
  field,
  value,
  error,
  onChange,
}: {
  field: FieldSchema
  value: unknown
  error: string | null
  onChange: (next: unknown) => void
}) {
  return (
    <Field
      label={`${field.label} (IST)`}
      hint="The date and time in India Standard Time. The site shows it as, for example, 29 September 2026, 09:30AM(IST)."
      error={error}
      labelAside={aside(field)}
    >
      {(control) => (
        <Input
          {...control}
          type="datetime-local"
          value={istLocal(value)}
          data-datetime={field.name}
          onChange={(e) => onChange(fromIstLocal(e.target.value))}
        />
      )}
    </Field>
  )
}

/**
 * **`slugs`** — a section's services (C25, ADR-0054 §5, T-4.135).
 *
 * The `ServicePicker` where the editor handed down the catalogue
 * (`ServiceCatalogProvider`); otherwise **the box**, one address a line, which
 * is what a user without `service:can_view` keeps. *Empty means all* exactly
 * where the schema does not require the field — `services`, `contact`,
 * `lead_form` — and the box's hint says which this field is, because "leave
 * empty to show everything" was wrong for `pricing` and `product_spotlight`,
 * which refuse an empty list.
 */
function SlugsField({
  field,
  value,
  error,
  onChange,
  pick = null,
  empties,
}: {
  field: FieldSchema
  value: unknown
  error: string | null
  onChange: (next: string[], pick?: ServicePick) => void
  /** C27: the stored pick, where the schema carries one. */
  pick?: ServicePick | null
  empties?: 'section' | 'form'
}) {
  const catalog = useServiceCatalog()
  const slugs = Array.isArray(value) ? (value as unknown[]).filter((s): s is string => typeof s === 'string') : []
  const max = field.max_items ?? 36
  const emptyMeansAll = !field.required
  if (catalog) {
    return (
      <ServicePicker
        label={field.label}
        value={slugs}
        max={max}
        emptyMeansAll={emptyMeansAll}
        pick={pick}
        empties={empties}
        catalog={catalog}
        error={error}
        onChange={onChange}
      />
    )
  }
  return (
    <Field label={field.label} hint={slugsBoxHint(max, emptyMeansAll)} error={error} labelAside={aside(field)}>
      {(control) => (
        <Textarea
          {...control}
          rows={3}
          value={slugs.join('\n')}
          onChange={(e) => {
            const next = e.target.value
              .split('\n')
              .map((line) => line.trim())
              .filter(Boolean)
            // The box's hint says empty shows everything, so where the schema
            // carries a pick (C27) the box keeps it true: empty is `all`.
            if (empties && emptyMeansAll) onChange(next, next.length === 0 ? 'all' : 'chosen')
            else onChange(next)
          }}
        />
      )}
    </Field>
  )
}

/** The box's hint, per field: what an empty box does here, and the limit. */
export function slugsBoxHint(max: number, emptyMeansAll: boolean): string {
  return emptyMeansAll
    ? `One service address per line, up to ${max}. Leave it empty to show every service on your website.`
    : `One service address per line — at least one, up to ${max}.`
}

/**
 * **`icon`** (C23, ADR-0045 §5): the picker, over the vocabulary's set. *No
 * icon* is **absent** (`undefined`), so a row saved without one is exactly the
 * row it was before C23; the service reads an empty value the same way.
 */
function IconField({
  field,
  value,
  error,
  onChange,
}: {
  field: FieldSchema
  value: unknown
  error: string | null
  onChange: (next: unknown) => void
}) {
  const set = useIconSet()
  return (
    <Field label={field.label} error={error} labelAside={aside(field)}>
      {(control) =>
        set ? (
          <IconPicker
            id={control.id}
            aria-describedby={control['aria-describedby']}
            label={field.label}
            value={typeof value === 'string' ? value : null}
            icons={set.icons}
            groups={set.groups}
            onChange={(key) => onChange(key ?? undefined)}
          />
        ) : (
          <p id={control.id} className="text-xs text-muted-foreground">
            The icon list could not be read. Reload the page to pick one.
          </p>
        )
      }
    </Field>
  )
}

function asString(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

/** "Required" or "optional", on the label's line. Every field says which, so a
 *  firm never has to press Save to find out. */
function aside(field: FieldSchema): ReactNode {
  return <span className="text-muted-foreground text-xs">{field.required ? 'required' : 'optional'}</span>
}

/** `header_layout` → "Header layout"; `full` → "Full". */
function sentence(value: string): string {
  const words = value.replace(/_/g, ' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/** The block types a firm may reasonably want on a page, labelled — used by the
 *  "add a block" menu so it does not have to re-derive them from the schema. */
export function blockLabel(schemas: BlockSchema[], type: string): string {
  return schemas.find((s) => s.type === type)?.label ?? type
}
